import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
  type ElementType,
  type HTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import classnames from 'src/util/classnames';
import { Button } from './Button';

// Reserved gap between a portaled menu and the viewport edge — kept in sync with the CSS
// max-width fallback below so a menu clamped to `EDGE_PADDING` never ends up wider than the
// space that clamp actually leaves it.
const EDGE_PADDING = 8;
const DEFAULT_MENU_MAX_WIDTH = `min(600px, calc(100vw - ${EDGE_PADDING * 2}px))`;

interface DropdownContextValue {
  isOpen: boolean;
  toggle: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  portalMenuRef: RefObject<HTMLElement | null>;
}

const DropdownContext = createContext<DropdownContextValue>({
  isOpen: false,
  toggle: () => {},
  anchorRef: { current: null },
  portalMenuRef: { current: null },
});

interface DropdownProps extends HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean;
  toggle?: () => void;
  direction?: 'up' | 'down' | 'start' | 'end' | 'left' | 'right';
  size?: 'sm' | 'lg';
  group?: boolean;
  nav?: boolean;
  tag?: ElementType;
  children?: ReactNode;
  active?: boolean;
}

function DropdownImpl({
  isOpen: controlledIsOpen,
  toggle: controlledToggle,
  direction = 'down',
  size,
  group,
  nav,
  tag,
  className,
  children,
  active,
  ...props
}: DropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  // Controlled when a toggle callback is provided — isOpen may start as undefined
  // (e.g. useState() with no initial value), so coerce undefined → false.
  const isControlled = controlledToggle !== undefined;
  const isOpen = isControlled ? (controlledIsOpen ?? false) : internalOpen;

  const toggle = () => {
    if (isControlled) controlledToggle?.();
    else setInternalOpen(prev => !prev);
  };

  const containerRef = useRef<HTMLElement>(null);
  const portalMenuRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insidePortal = portalMenuRef.current?.contains(target);
      if (!insideContainer && !insidePortal) {
        if (isControlled) controlledToggle?.();
        else setInternalOpen(false);
      }
    };
    document.addEventListener('mousedown', close, true);
    return () => document.removeEventListener('mousedown', close, true);
  }, [isOpen, isControlled, controlledToggle]);

  const dirClass = direction === 'up' ? 'dropup'
    : direction === 'start' || direction === 'left' ? 'dropstart'
    : direction === 'end' || direction === 'right' ? 'dropend'
    : undefined;

  const Tag = (tag ?? (nav ? 'li' : 'div')) as ElementType;

  return (
    <DropdownContext.Provider value={{ isOpen, toggle, anchorRef: containerRef, portalMenuRef }}>
      <Tag
        ref={containerRef}
        className={classnames(
          nav ? 'nav-item dropdown' : group ? 'btn-group' : 'dropdown',
          dirClass,
          size && `btn-group-${size}`,
          isOpen && 'show',
          className,
          {
            active,
          }
        )}
        {...props}
      >
        {children}
      </Tag>
    </DropdownContext.Provider>
  );
}

export const Dropdown = DropdownImpl;
export const UncontrolledDropdown = DropdownImpl;

interface DropdownToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tag?: ElementType;
  caret?: boolean;
  nav?: boolean;
  split?: boolean;
  color?: string;
  size?: 'sm' | 'lg';
  children?: ReactNode;
}

export const DropdownToggle = forwardRef<HTMLButtonElement, DropdownToggleProps>(function DropdownToggle(
  { tag, caret = true, nav, split, color, size, disabled, className, children, onClick, ...props },
  ref,
) {
  const { isOpen, toggle } = useContext(DropdownContext);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    toggle();
  };

  // `nav` renders as a `.nav-link`, not a button — a distinct visual variant Button doesn't
  // model, so it keeps its own plain-tag implementation.
  if (nav) {
    const Tag = (tag ?? 'a') as ElementType;
    return (
      <Tag
        ref={ref}
        type={Tag === 'button' ? 'button' : undefined}
        className={classnames('nav-link', caret && 'dropdown-toggle', isOpen && 'show', className)}
        disabled={Tag === 'button' ? disabled : undefined}
        aria-expanded={isOpen}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Tag>
    );
  }

  return (
    <Button
      ref={ref}
      tag={tag ?? 'button'}
      color={color}
      size={size}
      disabled={disabled}
      className={classnames(caret && 'dropdown-toggle', split && 'dropdown-toggle-split', isOpen && 'show', className)}
      aria-expanded={isOpen}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Button>
  );
});

interface DropdownMenuProps extends HTMLAttributes<HTMLUListElement> {
  end?: boolean;
  right?: boolean;
  flip?: boolean;
  container?: string | boolean;
  children?: ReactNode;
}

export function DropdownMenu({ end, right, flip: _flip = true, container, className, children, ...props }: DropdownMenuProps) {
  const { isOpen, anchorRef, portalMenuRef } = useContext(DropdownContext);
  const menuRef = useRef<HTMLUListElement>(null);

  // Register this portaled menu element so the close-outside handler
  // knows not to close when the user clicks inside it.
  useEffect(() => {
    if (!container) return;
    portalMenuRef.current = menuRef.current;
    return () => { portalMenuRef.current = null; };
  });

  useEffect(() => {
    if (!container || !isOpen || !anchorRef.current || !menuRef.current) return;

    const anchor = anchorRef.current;
    const menu  = menuRef.current;

    const position = () => {
      const rect = anchor.getBoundingClientRect();
      const menuW = menu.offsetWidth;
      const vw    = window.innerWidth;
      const vh    = window.innerHeight;

      // Prefer opening downward; flip up if not enough room below
      const spaceBelow = vh - rect.bottom;
      const spaceAbove = rect.top;
      const menuH      = menu.offsetHeight;
      const openUp     = spaceBelow < menuH && spaceAbove > spaceBelow;

      const top = openUp
        ? rect.top   - menuH - 2
        : rect.bottom + 2;

      // Prefer aligning to end (right) when end/right prop set, else start (left). If the
      // preferred side would overflow the viewport, try the other side; if neither fits
      // (e.g. a wide menu near either edge), fall back to centering on the viewport instead
      // of anchoring to the toggle at all.
      const fitsInViewport = (candidateLeft: number) => candidateLeft >= EDGE_PADDING && candidateLeft + menuW <= vw - EDGE_PADDING;

      const leftAligned = rect.left;
      const rightAligned = rect.right - menuW;
      const [preferred, fallback] = (end || right) ? [rightAligned, leftAligned] : [leftAligned, rightAligned];

      let left = fitsInViewport(preferred) ? preferred : fitsInViewport(fallback) ? fallback : (vw - menuW) / 2;

      // Final safety clamp in case the menu is wider than the viewport itself
      left = Math.min(left, vw - menuW - EDGE_PADDING);
      left = Math.max(left, EDGE_PADDING);

      menu.style.position = 'fixed';
      menu.style.top      = `${top}px`;
      menu.style.left     = `${left}px`;
    };

    position();

    window.addEventListener('scroll',  position, true);
    window.addEventListener('resize',  position);

    return () => {
      window.removeEventListener('scroll',  position, true);
      window.removeEventListener('resize',  position);
    };
  }, [container, isOpen, anchorRef, end, right]);

  const menu = (
    <ul
      ref={container ? menuRef : undefined}
      className={classnames('dropdown-menu', isOpen && 'show', (end || right) && 'dropdown-menu-end', className)}
      style={{ maxWidth: DEFAULT_MENU_MAX_WIDTH, ...(container ? { zIndex: 1100 } : null), ...props.style }}
      {...props}
    >
      {children}
    </ul>
  );

  if (container && isOpen) {
    return createPortal(menu, document.body);
  }

  return menu;
}

interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tag?: ElementType;
  header?: boolean;
  divider?: boolean;
  active?: boolean;
  children?: ReactNode;
}

export function DropdownItem({ tag, header, divider, active, disabled, className, children, onClick, ...props }: DropdownItemProps) {
  const { toggle } = useContext(DropdownContext);

  if (divider) return <li style={{ display: 'block', height: 0 }}><hr className={classnames('dropdown-divider', className)} /></li>;
  if (header) return <li><h6 className={classnames('dropdown-header', className)} {...props}>{children}</h6></li>;

  const Tag = (tag ?? 'button') as ElementType;
  return (
    <li style={{ width: '100%'}}>
      <Tag
        type={Tag === 'button' ? 'button' : undefined}
        className={classnames('dropdown-item', active && 'active', disabled && 'disabled', className)}
        disabled={Tag === 'button' ? disabled : undefined}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!disabled) {
            onClick?.(e);
            toggle();
          }
        }}
        {...props}
      >
        {children}
      </Tag>
    </li>
  );
}
